import { cn } from "@/common/utils";
// Paths from simple-icons (X, Reddit) and the local LinkedIn fallback, via hero-marks.
import {
  LINKEDIN_PATH,
  REDDIT_PATH,
  SALES_NAV_PATH,
  X_PATH,
} from "@/landing/lib/hero-marks";

export type Platform = "linkedin" | "sales-navigator" | "x" | "reddit";

export const PLATFORM_NAMES: Record<Platform, string> = {
  linkedin: "LinkedIn",
  "sales-navigator": "Sales Navigator",
  x: "X",
  reddit: "Reddit",
};

export const PLATFORM_PATHS: Record<Platform, string> = {
  linkedin: LINKEDIN_PATH,
  "sales-navigator": SALES_NAV_PATH,
  x: X_PATH,
  reddit: REDDIT_PATH,
};

/**
 * A platform's mark in the current text colour; decorative, so name it in text
 * beside it. Drawn from raw paths so no <title> tooltip shows on hover.
 */
export function PlatformMark({
  platform,
  className,
}: {
  platform: Platform;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className={cn("size-4 flex-none", className)}
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d={PLATFORM_PATHS[platform]}
      />
    </svg>
  );
}

export const AGENTS = [
  { name: "ChatGPT", icon: "/icons/openai.svg" },
  { name: "Codex", icon: "/icons/codex.svg" },
  { name: "Claude", icon: "/icons/claude.svg" },
  { name: "Claude Code", icon: "/icons/claude-code-bot.png" },
  { name: "OpenClaw", icon: "/icons/openclaw.svg" },
  { name: "Hermes", icon: "/icons/hermes.png" },
] as const;

// Agents with their own page that aren't in the "Works from" row.
const MORE_AGENTS = [{ name: "Cursor", icon: "/icons/cursor.svg" }] as const;

export type AgentName =
  | (typeof AGENTS)[number]["name"]
  | (typeof MORE_AGENTS)[number]["name"];

/** An agent's logo, inked to one colour like the rest of the system. */
export function AgentLogo({
  name,
  className,
}: {
  name: AgentName;
  className?: string;
}) {
  const agent = [...AGENTS, ...MORE_AGENTS].find(
    (entry) => entry.name === name,
  );
  if (!agent) return null;
  return (
    <img
      src={agent.icon}
      alt=""
      className={cn("h-4 w-auto opacity-80 [filter:brightness(0)]", className)}
    />
  );
}

/** "Works from" row: every supported agent's logo and name. */
export function AgentLogos({
  className,
  label = "Works from",
}: {
  className?: string;
  label?: string;
}) {
  return (
    <ul
      aria-label={label}
      className={cn(
        "m-0 flex list-none flex-wrap items-center gap-x-[15px] gap-y-2 p-0 text-[13.5px] text-(--g-ink-2)",
        className,
      )}
    >
      {label && <li className="text-(--g-ink-3)">{label}</li>}
      {AGENTS.map((agent) => (
        <li key={agent.name} className="inline-flex items-center gap-1.5">
          <AgentLogo name={agent.name} />
          {agent.name}
        </li>
      ))}
    </ul>
  );
}
